import { useState } from "react";
import { NavLink } from "react-router-dom";
import { LOGO } from "../lib/logo";
import { useAuth } from "../lib/auth";
import { BUILD, type Role } from "../lib/tipos";

const VERDE = "#0e8c3a";
const VERDE_CLARO = "#e8f5ee";

/** Atalhos principais — mesma ordem e mesmos ícones do sistema 4.x. */
const NAV: Array<{ para: string; icone: string; rotulo: string; minimo: Role }> = [
  { para: "/", icone: "📊", rotulo: "Dashboard", minimo: "LEITOR" },
  { para: "/visitas", icone: "📋", rotulo: "Todas as Visitas", minimo: "LEITOR" },
  { para: "/hoje", icone: "📅", rotulo: "Visitas de Hoje", minimo: "LEITOR" },
  { para: "/matriculas", icone: "✅", rotulo: "Matrículas", minimo: "LEITOR" },
  { para: "/nao-efetivadas", icone: "❌", rotulo: "Não Efetivadas", minimo: "LEITOR" },
  { para: "/retomada", icone: "🔄", rotulo: "Retomada", minimo: "LEITOR" },
  { para: "/orcamentos", icone: "📄", rotulo: "Orçamentos", minimo: "LEITOR" },
];

const ADMIN: Array<{ para: string; icone: string; rotulo: string; minimo: Role }> = [
  { para: "/valores/mensalidade", icone: "💰", rotulo: "Valores Mensalidade", minimo: "GESTOR" },
  { para: "/valores/material", icone: "📚", rotulo: "Valores Material", minimo: "GESTOR" },
  { para: "/valores/bilingue", icone: "🌎", rotulo: "Valores Bilíngue", minimo: "GESTOR" },
  { para: "/valores/minimos", icone: "🚫", rotulo: "Valores Mínimos", minimo: "GESTOR" },
  { para: "/usuarios", icone: "👥", rotulo: "Usuários", minimo: "ADMIN" },
];

function Item({ para, icone, rotulo, aberto }: { para: string; icone: string; rotulo: string; aberto: boolean }) {
  return (
    <NavLink
      to={para}
      end={para === "/"}
      title={aberto ? undefined : rotulo}
      style={({ isActive }) => ({
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        borderRadius: 8,
        marginBottom: 2,
        textDecoration: "none",
        whiteSpace: "nowrap",
        overflow: "hidden",
        fontSize: 13,
        background: isActive ? VERDE_CLARO : "transparent",
        color: isActive ? VERDE : "#475467",
        fontWeight: isActive ? 600 : 500,
      })}
    >
      <span style={{ fontSize: 16, flexShrink: 0 }}>{icone}</span>
      {aberto && <span>{rotulo}</span>}
    </NavLink>
  );
}

const CHAVE_MENU = "ag5.menuAberto";

export function Layout({ children }: { children: React.ReactNode }) {
  const { usuario, sair, podeAoMenos } = useAuth();
  // lembra a preferência: quem trabalha em tela pequena não quer reabrir o menu toda vez
  const [aberto, setAberto] = useState(() => localStorage.getItem(CHAVE_MENU) !== "0");

  function alternar() {
    setAberto((a) => {
      localStorage.setItem(CHAVE_MENU, a ? "0" : "1");
      return !a;
    });
  }

  return (
    <div style={{ display: "flex", height: "100vh", background: "#f4f6fa", color: "#1d2939", overflow: "hidden" }}>
      <aside
        style={{
          width: aberto ? 196 : 56,
          background: "#fff",
          borderRight: "1px solid #e4e7ec",
          display: "flex",
          flexDirection: "column",
          transition: "width .25s",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {/* clicar no cabeçalho recolhe e expande, como no 4.x */}
        <button
          onClick={alternar}
          title={aberto ? "Recolher menu" : "Expandir menu"}
          style={{
            padding: aberto ? "14px 12px" : "14px 10px",
            borderBottom: "1px solid #e4e7ec",
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
            background: "none",
            border: "none",
            borderRadius: 0,
            textAlign: "left",
            width: "100%",
          }}
        >
          <img src={LOGO} alt="COC" style={{ width: 34, flexShrink: 0 }} />
          {aberto && (
            <div style={{ overflow: "hidden", whiteSpace: "nowrap" }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: VERDE }}>Colégio COCSJC</div>
              <div style={{ fontSize: 9.5, color: "#98a2b3" }}>Unidade SUL — Visitas</div>
            </div>
          )}
        </button>

        <nav style={{ flex: 1, padding: "8px 6px", overflowY: "auto" }}>
          {NAV.filter((n) => podeAoMenos(n.minimo)).map((n) => (
            <Item key={n.para} {...n} aberto={aberto} />
          ))}

          {ADMIN.some((n) => podeAoMenos(n.minimo)) && (
            <>
              {aberto && (
                <div style={{ padding: "12px 12px 6px", fontSize: 10, color: "#98a2b3", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
                  Administração
                </div>
              )}
              {ADMIN.filter((n) => podeAoMenos(n.minimo)).map((n) => (
                <Item key={n.para} {...n} aberto={aberto} />
              ))}
            </>
          )}
        </nav>

        <div style={{ borderTop: "1px solid #e4e7ec", padding: "12px 14px" }}>
          {aberto ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                {usuario?.nome}
              </div>
              <div style={{ fontSize: 10, color: "#98a2b3", textTransform: "uppercase", letterSpacing: .5 }}>
                {usuario?.role}
              </div>
            </>
          ) : null}
          <button
            onClick={sair}
            title="Sair"
            style={{ marginTop: 8, background: "none", border: "none", color: "#667085", cursor: "pointer", padding: 0, fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}
          >
            <span style={{ fontSize: 16 }}>⏻</span>
            {aberto && <span>Sair</span>}
          </button>
          {aberto && (
            <div
              title="Data do build que está no seu navegador. Se não mudou depois de um deploy, o deploy não trouxe código novo."
              style={{ fontSize: 9, color: "#cbd2d9", marginTop: 10, letterSpacing: .3 }}
            >
              build {BUILD}
            </div>
          )}
        </div>
      </aside>

      <main style={{ flex: 1, overflow: "auto", padding: "18px 20px" }}>{children}</main>
    </div>
  );
}

/** Cabeçalho padrão das páginas: título à esquerda, data e usuário à direita. */
export function Cabecalho({ titulo, acao }: { titulo: string; acao?: React.ReactNode }) {
  const { usuario } = useAuth();
  const hoje = new Date().toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
      <h1 style={{ fontSize: 24, margin: 0, flex: 1 }}>{titulo}</h1>
      <div style={{ fontSize: 13, color: "#667085" }}>{hoje}</div>
      <div style={{ background: VERDE_CLARO, borderRadius: 10, padding: "8px 14px", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16 }}>{usuario?.role === "ADMIN" ? "👑" : "👤"}</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: VERDE }}>{usuario?.email}</div>
          <div style={{ fontSize: 10, color: "#667085", textTransform: "uppercase", letterSpacing: .5 }}>
            {usuario?.role}
          </div>
        </div>
      </div>
      {acao}
    </div>
  );
}
